/**
 * A file field that uploads first (`x-cis-field.upload`: the supplier's quote document). Picking a file sends it to
 * the field's upload route through the object UI adapter; the field takes the ref the route answers, so the command
 * and its dry run only see the ref. Uploading, a refusal (before sending, or the route's own) with a way to try
 * again, and the file kept with a way to replace it. The ref stays in the field while a replacement uploads, so
 * the command stays runnable until the new file is kept.
 *
 * `UploadFilesField` is the several-files mode (a `list[str]` field with the same `upload` target): a list of files,
 * each uploaded in turn through the same route with its own uploading, refused (with Try again) and kept state, a
 * remove button per file, and an Add files button that picks several at once. The field holds the refs of the kept
 * files in the order chosen, so the command stays runnable while one more uploads.
 *
 * Both take a pasted screenshot as well as a chosen file (see `pasteScreenshot.tsx`).
 */
import { useRef, useState } from "react";
import { FileText, RotateCcw, X } from "lucide-react";
import { ApiError } from "@shared-core/data/api/errors";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { extractFieldErrors } from "@shared-core/data/validation";
import type {
  ChosenFile,
  UploadTarget,
} from "@shared-core/domain/object-ui/contract";
import { formatBytes } from "@shared-core/domain/formatters";
import {
  countedFiles,
  entriesFromRefs,
  keptRefsOf,
  settleEntry,
  tooManyFilesRefusal,
  uploadRefusal,
  withoutEntry,
  type FileEntry,
} from "@shared-core/domain/object-ui/upload-field";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Progress } from "../../primitives/progress";
import { describedByHint, usePasteScreenshot } from "./pasteScreenshot";

type UploadState =
  | { status: "idle" }
  | { status: "uploading"; file: ChosenFile }
  | { status: "failed"; file: ChosenFile | null; message: string }
  | { status: "done"; name: string; size: number };

/** The route's own refusal in its words where it gave some; else a plain fallback. */
function failureOf(err: unknown): string {
  const [first] = extractFieldErrors(err);
  if (first) return first.message;
  if (err instanceof ApiError && err.detail) return err.detail;
  return "The file couldn't be uploaded.";
}
export function UploadField({
  id,
  upload,
  accept,
  maxBytes,
  value,
  disabled,
  invalid = {},
  onChange,
}: {
  id: string;
  upload: UploadTarget;
  accept: string[] | null;
  maxBytes: number | null;
  /** The ref the field holds (an earlier upload's, or a prefill). */
  value: string | null;
  disabled: boolean;
  /** `aria-invalid` / `aria-describedby` from the field's errors. */
  invalid?: Record<string, unknown>;
  onChange: (ref: string) => void;
}) {
  const adapter = useObjectUiAdapter();
  const [state, setState] = useState<UploadState>(
    value
      ? { status: "done", name: "File uploaded", size: -1 }
      : { status: "idle" },
  );
  const [isPicking, setPicking] = useState(!value);
  const statusId = `${id}-upload-status`;
  const paste = usePasteScreenshot({
    id,
    accept,
    disabled: disabled || state.status === "uploading",
    onFile: (file) => void send(file),
  });

  const send = async (file: ChosenFile) => {
    const refused = uploadRefusal(
      { name: file.name, size: file.size, type: file.blob.type },
      accept,
      maxBytes,
    );
    if (refused)
      return setState({ status: "failed", file: null, message: refused });
    setState({ status: "uploading", file });
    try {
      const ref = await adapter.upload(upload, file);
      setState({ status: "done", name: file.name, size: file.size });
      setPicking(false);
      onChange(ref);
    } catch (err) {
      setState({ status: "failed", file, message: failureOf(err) });
    }
  };

  return (
    <div className="space-y-1.5" onPaste={paste.onPaste}>
      {state.status === "done" && !isPicking && (
        <div className="flex items-center gap-2 text-sm">
          <FileText className="size-4 text-muted-foreground" aria-hidden />
          <span role="status" id={statusId}>
            {state.name}
            {state.size >= 0 && (
              <span className="text-muted-foreground">
                {" "}
                · {formatBytes(state.size)}
              </span>
            )}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-auto h-7 px-2"
            disabled={disabled}
            onClick={() => setPicking(true)}
          >
            Replace file
          </Button>
        </div>
      )}
      {isPicking && (
        <Input
          id={id}
          type="file"
          accept={accept?.join(",")}
          disabled={disabled || state.status === "uploading"}
          {...describedByHint(invalid, paste.hintId)}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file)
              void send({
                kind: "chosen_file",
                name: file.name,
                size: file.size,
                blob: file,
              });
          }}
        />
      )}
      {paste.hint}
      {state.status === "uploading" && (
        <div role="status" className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Uploading {state.file.name}…
          </p>
          <Progress
            value={100}
            aria-label={`Uploading ${state.file.name}`}
            className="h-1.5 animate-pulse"
          />
        </div>
      )}
      {state.status === "failed" && (
        <div
          role="alert"
          className="flex items-start gap-2 text-sm text-red-600"
        >
          <span>{state.message}</span>
          {state.file && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 shrink-0 px-2"
              disabled={disabled}
              onClick={() => state.file && void send(state.file)}
            >
              <RotateCcw className="size-3.5" aria-hidden />
              Try again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

const sameRefs = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((ref, i) => ref === b[i]);

export function UploadFilesField({
  id,
  upload,
  accept,
  maxBytes,
  maxFiles,
  value,
  disabled,
  invalid = {},
  onChange,
}: {
  id: string;
  upload: UploadTarget;
  accept: string[] | null;
  /** Per file. */
  maxBytes: number | null;
  maxFiles: number | null;
  /** The refs the field holds (earlier uploads', or a prefill). */
  value: string[];
  disabled: boolean;
  invalid?: Record<string, unknown>;
  /** Called with the kept files' refs, in order, whenever they change. */
  onChange: (refs: string[]) => void;
}) {
  const adapter = useObjectUiAdapter();
  const [entries, setEntries] = useState<FileEntry[]>(() =>
    entriesFromRefs(value, []),
  );
  // The latest entries, for the uploads that settle after the render that started them.
  const latest = useRef(entries);
  const chosen = useRef(new Map<string, ChosenFile>());
  const lastId = useRef(0);
  const turn = useRef<Promise<void>>(Promise.resolve());
  const picker = useRef<HTMLInputElement>(null);
  const [countMessage, setCountMessage] = useState<string | null>(null);
  const paste = usePasteScreenshot({
    id,
    accept,
    disabled,
    onFile: (file) => add([file]),
  });

  const show = (next: FileEntry[]) => {
    const before = keptRefsOf(latest.current);
    latest.current = next;
    setEntries(next);
    const after = keptRefsOf(next);
    if (!sameRefs(before, after)) onChange(after);
  };

  // The form re-read the field (a prefill arrived): show those files, unless one is mid-upload or refused here.
  if (
    entries.every((entry) => entry.status === "kept") &&
    !sameRefs(value, keptRefsOf(entries))
  ) {
    const next = entriesFromRefs(value, entries);
    latest.current = next;
    setEntries(next);
  }

  const settle = (next: FileEntry) => {
    // The file was removed while it uploaded: its answer is not wanted.
    if (latest.current.some((entry) => entry.id === next.id))
      show(settleEntry(latest.current, next));
  };

  const sendNow = async (entryId: string) => {
    const file = chosen.current.get(entryId);
    if (!file || !latest.current.some((entry) => entry.id === entryId)) return;
    try {
      const ref = await adapter.upload(upload, file);
      settle({
        id: entryId,
        status: "kept",
        name: file.name,
        size: file.size,
        ref,
      });
    } catch (err) {
      settle({
        id: entryId,
        status: "refused",
        name: file.name,
        message: failureOf(err),
        retryable: true,
      });
    }
  };
  // One file at a time, in the order chosen.
  const send = (entryId: string) => {
    turn.current = turn.current.then(() => sendNow(entryId));
  };

  const add = (picked: ChosenFile[]) => {
    const tooMany = tooManyFilesRefusal(
      countedFiles(latest.current),
      picked.length,
      maxFiles,
    );
    setCountMessage(tooMany);
    if (tooMany) return;
    const added = picked.map((file): FileEntry => {
      const entryId = `file-${lastId.current++}`;
      const refused = uploadRefusal(
        { name: file.name, size: file.size, type: file.blob.type },
        accept,
        maxBytes,
      );
      if (refused)
        return {
          id: entryId,
          status: "refused",
          name: file.name,
          message: refused,
          retryable: false,
        };
      chosen.current.set(entryId, file);
      return { id: entryId, status: "uploading", name: file.name };
    });
    show([...latest.current, ...added]);
    for (const entry of added) if (entry.status === "uploading") send(entry.id);
  };

  const retry = (entry: FileEntry) => {
    show(
      settleEntry(latest.current, {
        id: entry.id,
        status: "uploading",
        name: entry.name,
      }),
    );
    send(entry.id);
  };

  const remove = (entry: FileEntry) => {
    chosen.current.delete(entry.id);
    setCountMessage(null);
    show(withoutEntry(latest.current, entry.id));
  };

  return (
    <div className="space-y-1.5" onPaste={paste.onPaste}>
      {entries.length > 0 && (
        <ul aria-label="Files" className="space-y-1.5">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-start gap-2 text-sm"
              data-status={entry.status}
            >
              <FileText
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0 flex-1 break-words">
                {entry.status === "kept" && (
                  <span>
                    {entry.name}
                    {entry.size !== null && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {formatBytes(entry.size)}
                      </span>
                    )}
                  </span>
                )}
                {entry.status === "uploading" && (
                  <div role="status" className="space-y-1">
                    <p className="text-muted-foreground">
                      Uploading {entry.name}…
                    </p>
                    <Progress
                      value={100}
                      aria-label={`Uploading ${entry.name}`}
                      className="h-1.5 animate-pulse"
                    />
                  </div>
                )}
                {entry.status === "refused" && (
                  <div role="alert" className="text-red-600">
                    {entry.message}
                  </div>
                )}
              </div>
              {entry.status === "refused" && entry.retryable && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 px-2"
                  disabled={disabled}
                  onClick={() => retry(entry)}
                >
                  <RotateCcw className="size-3.5" aria-hidden />
                  Try again
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 shrink-0 px-2"
                disabled={disabled}
                aria-label={`Remove ${entry.name}`}
                onClick={() => remove(entry)}
              >
                <X className="size-3.5" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={id}
        ref={picker}
        type="file"
        multiple
        accept={accept?.join(",")}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        {...describedByHint(invalid, paste.hintId)}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []).map(
            (file): ChosenFile => ({
              kind: "chosen_file",
              name: file.name,
              size: file.size,
              blob: file,
            }),
          );
          // The same file may be picked again after it is removed.
          e.target.value = "";
          if (picked.length > 0) add(picked);
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => picker.current?.click()}
      >
        Add files
      </Button>
      {paste.hint}
      {maxFiles !== null && (
        <p className="text-xs text-muted-foreground">
          Up to {maxFiles} {maxFiles === 1 ? "file" : "files"}.
        </p>
      )}
      {countMessage && (
        <p role="alert" className="text-sm text-red-600">
          {countMessage}
        </p>
      )}
    </div>
  );
}
